/**
 * Browser-side token + PKCE-verifier storage.
 *
 * **Round-23 storage swap.** Tokens now live in `localStorage`
 * rather than `sessionStorage` so a signed-in visitor survives tab
 * close, browser restart, and cross-tab sign-in. The operator asked
 * for persistent sessions (repeatedly re-signing-in is a poor
 * experience). The PKCE code verifier still lives in
 * `sessionStorage` — the verifier is single-use per sign-in attempt
 * and must not persist across sessions (that would compound a
 * potential CSRF surface).
 *
 * Pre-round-23 design (see git history under
 * "public-website: swap token storage to localStorage") kept both
 * tokens and verifier in `sessionStorage`. The verifier's storage
 * is intentionally unchanged; only the token record swapped.
 *
 * The module also stores the **PKCE code verifier** that
 * `<SignInPage>` (task 7.6) writes before redirecting to the Hosted
 * UI's `/oauth2/authorize` endpoint, and that `<OAuthCallbackPage>`
 * reads on the redirect back. The verifier lives under a separate
 * key prefix so a stale verifier (from a never-completed sign-in
 * attempt) cannot be mistaken for a token record, and so calling
 * {@link clearTokens} after sign-out does not also clobber an
 * in-flight verifier.
 *
 * Storage keys are namespaced by the **Cognito user pool client id**
 * so a future change of the SPA's app client id (or running the SPA
 * pointed at a different Cognito pool, e.g. local dev vs beta vs
 * prod) cannot collide — a record stored under client A is opaque
 * when the SPA boots with client B.
 *
 * Function-length budget: every export below stays under 20 lines per
 * Requirement 3.7.
 */

/**
 * One Cognito-issued token record per user pool client id.
 *
 * Shape mirrors the Hosted UI's `/oauth2/token` response, with
 * `expiresAt` precomputed by the caller as `Date.now() + expires_in *
 * 1000` so the auth slice's "is the token still valid" check is a
 * trivial `Date.now() < expiresAt` comparison.
 */
export interface StoredTokens {
  /** Cognito ID token (the JWT). */
  readonly idToken: string;
  /** Cognito access token (used by the Hosted UI's user-info endpoint). */
  readonly accessToken: string;
  /**
   * Optional Cognito refresh token. Cognito may not return one for
   * SPA / public clients depending on app-client configuration; the
   * SPA treats absence as "no silent refresh" and re-prompts the
   * Hosted UI when the access window expires.
   */
  readonly refreshToken?: string;
  /** Epoch milliseconds at which the access window expires. */
  readonly expiresAt: number;
}

/**
 * Key prefix for the token record. The full key is
 * `dvgup-tokens-${userPoolClientId}` so multi-tenant (or multi-stage)
 * SPAs cannot collide.
 */
const TOKENS_KEY_PREFIX = 'dvgup-tokens-';

/**
 * Key prefix for the PKCE code verifier `<SignInPage>` writes before
 * redirecting and `<OAuthCallbackPage>` reads on return. Separate
 * from {@link TOKENS_KEY_PREFIX} so the two lifecycles do not
 * interfere — see module-level TSDoc for rationale.
 */
const PKCE_VERIFIER_KEY_PREFIX = 'dvgup-pkce-verifier-';

/**
 * Build the full sessionStorage key for the supplied prefix + client id.
 *
 * Internal helper kept private to keep the public surface limited to
 * the six CRUD functions. Concatenation is deliberate — the client id
 * is treated as opaque and is not URL-encoded.
 */
function buildKey(prefix: string, userPoolClientId: string): string {
  return `${prefix}${userPoolClientId}`;
}

/**
 * Load the token record for the supplied Cognito user pool client id.
 *
 * Per **design §2.3**, this is how the auth slice rehydrates after a
 * full-page reload within the same tab/session. Returns `null` when:
 *
 * - sessionStorage has no entry for the supplied client id (the SPA
 *   has never signed in, or the user signed out, or this is a fresh
 *   tab — sessionStorage is not shared across tabs).
 * - The stored value is malformed JSON. Surfacing `null` rather than
 *   throwing keeps the boot path resilient to a corrupted record;
 *   the SPA simply treats the user as unauthenticated and shows the
 *   "Sign in" surface.
 *
 * @param userPoolClientId Cognito SPA app client id under which the
 *   record was previously written. Used as the namespacing component
 *   of the storage key.
 * @returns The previously-stored {@link StoredTokens} record, or
 *   `null` when the key is missing or the stored payload is
 *   malformed.
 */
export function loadTokens(userPoolClientId: string): StoredTokens | null {
  const key = buildKey(TOKENS_KEY_PREFIX, userPoolClientId);
  // Round-23: tokens now live in localStorage. Read localStorage
  // first; fall back to sessionStorage during the transition window
  // so users signed in under the pre-round-23 build stay signed in
  // after the deploy without re-authenticating. The fallback line
  // can be removed one deploy cycle after this ships.
  const raw = localStorage.getItem(key) ?? sessionStorage.getItem(key);
  if (raw === null) return null;
  try {
    return JSON.parse(raw) as StoredTokens;
  } catch {
    return null;
  }
}

/**
 * Persist a token record under the supplied Cognito user pool client id.
 *
 * Per **design §2.3** ("persist tokens in sessionStorage; dispatch
 * authSignedIn"), this is the side effect the
 * `<OAuthCallbackPage>` performs after a successful PKCE exchange,
 * before dispatching the `authSignedIn` action.
 *
 * @param userPoolClientId Cognito SPA app client id used to namespace
 *   the storage key.
 * @param tokens The token record to persist. JSON-stringified and
 *   written verbatim.
 */
export function storeTokens(userPoolClientId: string, tokens: StoredTokens): void {
  const key = buildKey(TOKENS_KEY_PREFIX, userPoolClientId);
  localStorage.setItem(key, JSON.stringify(tokens));
  // Round-23 transition: drop any stale sessionStorage entry left
  // over from the pre-round-23 build so `loadTokens` can't return an
  // out-of-date record after a fresh sign-in.
  sessionStorage.removeItem(key);
}

/**
 * Remove the token record for the supplied Cognito user pool client id.
 *
 * Per **design §2.3**, this is the sign-out side effect — the
 * AppShell's "Sign out" flow clears the tokens and dispatches
 * `authSignedOut` so the SPA returns to the unauthenticated UI.
 * Idempotent: removing a missing key is a no-op.
 *
 * @param userPoolClientId Cognito SPA app client id under which the
 *   record was written.
 */
export function clearTokens(userPoolClientId: string): void {
  const key = buildKey(TOKENS_KEY_PREFIX, userPoolClientId);
  localStorage.removeItem(key);
  sessionStorage.removeItem(key);
}

/**
 * Persist the PKCE code verifier `<SignInPage>` generates before
 * redirecting to the Cognito Hosted UI's `/oauth2/authorize`
 * endpoint.
 *
 * Per **design §2.3**, the verifier survives the redirect-and-return
 * round trip in `sessionStorage` (single tab, single session). The
 * `<OAuthCallbackPage>` reads it via {@link loadCodeVerifier} to
 * exchange the authorization `code` at `/oauth2/token`. Stored under
 * the {@link PKCE_VERIFIER_KEY_PREFIX} prefix so the verifier
 * lifecycle is independent of the token lifecycle.
 *
 * @param userPoolClientId Cognito SPA app client id used to namespace
 *   the storage key.
 * @param verifier Base64url-encoded code verifier from
 *   `lib/auth/pkce.ts#generateCodeVerifier`.
 */
export function storeCodeVerifier(userPoolClientId: string, verifier: string): void {
  sessionStorage.setItem(
    buildKey(PKCE_VERIFIER_KEY_PREFIX, userPoolClientId),
    verifier,
  );
}

/**
 * Load the PKCE code verifier previously written by
 * {@link storeCodeVerifier}.
 *
 * Per **design §2.3**, this is what `<OAuthCallbackPage>` reads when
 * the Hosted UI redirects back with `?code=...`. Returns `null` when
 * no verifier was stored (e.g. the user opened `/oauth-callback`
 * directly without going through `/sign-in`); the callback page
 * treats `null` as a hard error and surfaces the failure to the user.
 *
 * @param userPoolClientId Cognito SPA app client id under which the
 *   verifier was written.
 * @returns The previously-stored verifier string, or `null` when the
 *   key is missing.
 */
export function loadCodeVerifier(userPoolClientId: string): string | null {
  return sessionStorage.getItem(buildKey(PKCE_VERIFIER_KEY_PREFIX, userPoolClientId));
}

/**
 * Remove the PKCE code verifier for the supplied Cognito user pool
 * client id.
 *
 * Per **design §2.3**, the verifier is single-use — once
 * `<OAuthCallbackPage>` has exchanged the authorization code at
 * `/oauth2/token`, the verifier serves no further purpose and is
 * cleared so a stale value cannot be mistaken for a fresh sign-in
 * attempt. Idempotent: removing a missing key is a no-op.
 *
 * @param userPoolClientId Cognito SPA app client id under which the
 *   verifier was written.
 */
export function clearCodeVerifier(userPoolClientId: string): void {
  sessionStorage.removeItem(buildKey(PKCE_VERIFIER_KEY_PREFIX, userPoolClientId));
}
